import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { Copy, Zap, X, ArrowLeft, Bookmark, ExternalLink, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Fill() {
  const { user, loading: authLoading } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const appId = params.get('app');
  const isPopup = window.opener !== null || window.name === 'lifeAdminFill';

  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [selectedAppId, setSelectedAppId] = useState(appId || 'profile');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      if (selectedAppId === 'profile') {
        const [profileRes, missingRes] = await Promise.all([
          axios.get(`${API}/profile`, { withCredentials: true }),
          axios.get(`${API}/profile/missing`, { withCredentials: true }).catch(() => null),
        ]);
        const p = profileRes.data;
        const identity = p.identity || {};
        const work = p.work || {};
        const fin = p.financials || {};
        const addr = (identity.addresses && identity.addresses[0]) || {};
        setData({
          full_name: identity.full_name || '',
          first_name: identity.first_name || '',
          last_name: identity.last_name || '',
          email: identity.email || user?.email || '',
          phone: identity.phone || '',
          date_of_birth: identity.date_of_birth || '',
          nationality: identity.nationality || '',
          gender: identity.gender || '',
          address: addr.full_address || '',
          current_address: addr.full_address || '',
          current_employer: work.current_employer || '',
          job_title: work.job_title || '',
          years_experience: work.years_experience || '',
          annual_income: fin.annual_income || '',
          bank_name: fin.bank_name || '',
          tax_id: fin.tax_id || '',
          skills: Array.isArray(work.skills) ? work.skills.join(', ') : '',
        });
      } else {
        const res = await axios.get(`${API}/applications/${selectedAppId}`, { withCredentials: true });
        setData(res.data.form_data || {});
      }
    } catch (e) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [selectedAppId, user]);

  const fetchApplications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/applications`, { withCredentials: true });
      setApplications(res.data);
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (!authLoading && user) {
      fetchData();
      fetchApplications();
    }
  }, [authLoading, user, fetchData, fetchApplications]);

  const copyField = async (key, value) => {
    if (value === '' || value === null || value === undefined) return;
    const stringVal = typeof value === 'object' ? JSON.stringify(value) : String(value);
    try {
      await navigator.clipboard.writeText(stringVal);
      toast.success(`Copied ${key.replace(/_/g, ' ')}`);
    } catch {
      toast.error('Copy failed');
    }
  };

  const bookmarklet = `javascript:(function(){window.open('${window.location.origin}/fill','lifeAdminFill','width=420,height=760,resizable=yes,scrollbars=yes');})();`;

  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col gap-4 p-6">
        <p className="text-sm text-muted-foreground uppercase tracking-widest">Sign in to use Fill Helper</p>
        <Button onClick={() => navigate('/')} variant="outline" size="sm">Go to Home</Button>
      </div>
    );
  }

  const entries = Object.entries(data).filter(([, v]) => v !== '' && v !== null && v !== undefined);

  return (
    <div className={`${isPopup ? 'p-4' : 'container mx-auto px-4 md:px-8 py-8 max-w-2xl'}`} data-testid="fill-helper-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" />
            <h1 className="font-heading text-xl">FILL HELPER</h1>
          </div>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
            Click any field to copy · Paste on the real form
          </p>
        </div>
        {isPopup ? (
          <Button size="icon" variant="ghost" onClick={() => window.close()} data-testid="close-popup-btn">
            <X className="w-4 h-4" />
          </Button>
        ) : (
          <Button size="sm" variant="ghost" onClick={() => navigate('/dashboard')} className="uppercase text-xs tracking-wider">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        )}
      </div>

      {/* Source selector */}
      <div className="mb-4">
        <label className="text-[10px] uppercase tracking-widest text-muted-foreground block mb-1">DATA SOURCE</label>
        <select
          value={selectedAppId}
          onChange={(e) => setSelectedAppId(e.target.value)}
          className="w-full border border-border bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring"
          data-testid="fill-source-select"
        >
          <option value="profile">My Life Profile (general)</option>
          {applications.map((a) => (
            <option key={a.application_id} value={a.application_id}>
              {a.title} ({a.filled_fields}/{a.total_fields})
            </option>
          ))}
        </select>
      </div>

      {/* Field grid */}
      {loading ? (
        <div className="py-16 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : entries.length === 0 ? (
        <div className="border border-dashed border-border p-8 text-center">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">
            No data yet. Upload a document or fill your profile first.
          </p>
        </div>
      ) : (
        <div className="border border-border" data-testid="fill-fields-list">
          {entries.map(([k, v]) => {
            const display = Array.isArray(v) ? v.join(', ') : String(v);
            return (
              <button
                key={k}
                onClick={() => copyField(k, v)}
                className="w-full text-left px-3 py-2.5 border-b border-border/60 last:border-b-0 hover:bg-secondary/30 transition-colors flex items-center justify-between gap-3 group"
                data-testid={`fill-copy-${k}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                    <Copy className="w-2.5 h-2.5 opacity-50 group-hover:opacity-100" />
                    {k.replace(/_/g, ' ')}
                  </div>
                  <div className="font-mono text-xs mt-0.5 break-words">{display}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Bookmarklet section — only show in non-popup mode */}
      {!isPopup && (
        <div className="mt-8 border border-border p-5" data-testid="bookmarklet-section">
          <div className="flex items-center gap-2 mb-2">
            <Bookmark className="w-4 h-4 text-primary" />
            <h2 className="font-heading text-sm uppercase tracking-widest">ONE-CLICK BOOKMARKLET</h2>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Drag the button below into your bookmarks bar. Then when you're on any rental / job / loan form, click the bookmark → this Fill Helper opens in a small window next to the form → click any field to copy → paste in the real form.
          </p>
          <a
            href={bookmarklet}
            onClick={(e) => e.preventDefault()}
            draggable
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground uppercase text-xs tracking-widest font-bold cursor-grab active:cursor-grabbing hover:opacity-90"
            data-testid="bookmarklet-drag"
          >
            <Zap className="w-3.5 h-3.5" /> DRAG ME → BOOKMARKS BAR
          </a>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-3">
            Doesn't work? On mobile? Use the "Open Fill Helper" button on the Applications page instead.
          </p>
        </div>
      )}

      {!isPopup && (
        <div className="mt-6 text-center">
          <a
            href="/applications"
            className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          >
            Go to Applications <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      )}
    </div>
  );
}
